import { ContentSafetyButton } from './AccountSafety';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import socket, { acquireSocketConnection, releaseSocketConnection } from '../../socket';
import { useTranslation } from '../i18n/useTranslation';
import { queryKeys } from '../lib/queryClient';
import {
  clearCurrentAnnouncement,
  getCurrentAnnouncement,
  saveCurrentAnnouncement,
} from '../services/announcementsApi';
import { DeleteIcon, EditIcon, WriteIcon } from './icons';
import styles from './AnnouncementPanel.module.css';

const DATE_LOCALES = {
  ko: 'ko-KR',
  en: 'en-GB',
  fr: 'fr-FR',
};
const ANNOUNCEMENT_QUERY_KEY = queryKeys.announcements.current();

const formatUpdatedAt = (dateValue, lang) => {
  if (!dateValue) return '';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(DATE_LOCALES[lang] || DATE_LOCALES.ko, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Paris',
  }).format(date);
};

export default function AnnouncementPanel({ currentTheme }) {
  const { t, lang } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = ANNOUNCEMENT_QUERY_KEY;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [formError, setFormError] = useState('');

  const {
    data,
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: getCurrentAnnouncement,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const announcement = data?.announcement || null;
  const canManage = data?.can_manage === true;
  const maxContentLength = data?.limits?.max_content_length;

  useEffect(() => {
    acquireSocketConnection();

    const handleAnnouncementUpdated = ({ announcement: updatedAnnouncement, invalidate } = {}) => {
      if(invalidate) { queryClient.invalidateQueries({queryKey}); return; }
      const existingData = queryClient.getQueryData(queryKey);

      if (existingData) {
        queryClient.setQueryData(queryKey, {
          ...existingData,
          announcement: updatedAnnouncement || null,
        });
      } else {
        queryClient.invalidateQueries({ queryKey });
      }
    };

    socket.on('announcementUpdated', handleAnnouncementUpdated);

    return () => {
      socket.off('announcementUpdated', handleAnnouncementUpdated);
      releaseSocketConnection();
    };
  }, [queryClient, queryKey]);

  useEffect(() => {
    if (!isEditing) {
      setDraft(announcement?.content || '');
      setFormError('');
    }
  }, [announcement?.content, isEditing]);

  useEffect(() => {
    if (announcement && window.location.hash === '#main-announcement') {
      document.getElementById('main-announcement')?.scrollIntoView?.({ block: 'center' });
    }
  }, [announcement]);

  const saveMutation = useMutation({
    mutationFn: saveCurrentAnnouncement,
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response);
      setIsEditing(false);
      setFormError('');
    },
    onError: () => {
      setFormError(t('announcement.saveError'));
    },
  });

  const clearMutation = useMutation({
    mutationFn: clearCurrentAnnouncement,
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response);
      setIsEditing(false);
      setDraft('');
      setFormError('');
    },
    onError: () => {
      setFormError(t('announcement.clearError'));
    },
  });

  const beginEditing = () => {
    setDraft(announcement?.content || '');
    setFormError('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(announcement?.content || '');
    setFormError('');
    setIsEditing(false);
  };

  const handleSave = (event) => {
    event.preventDefault();
    const content = draft.trim();

    if (!content) {
      setFormError(t('announcement.contentRequired'));
      return;
    }

    if (maxContentLength && content.length > maxContentLength) {
      setFormError(t('announcement.contentTooLong', { max: maxContentLength }));
      return;
    }

    saveMutation.mutate(content);
  };

  const handleClear = () => {
    if (window.confirm(t('announcement.clearConfirm'))) {
      clearMutation.mutate();
    }
  };

  if (isLoading) return null;

  if (!announcement && !canManage && !isError) return null;

  const updatedAt = formatUpdatedAt(announcement?.updated_at, lang);
  const isMutating = saveMutation.isPending || clearMutation.isPending;
  const announcementActionLabel = announcement
    ? t('announcement.edit')
    : t('announcement.write');
  const clearActionLabel = clearMutation.isPending
    ? t('announcement.clearing')
    : t('announcement.clear');

  return (
    <section
      id="main-announcement"
      className={styles.panel}
      aria-labelledby="main-announcement-title"
      style={{
        '--announcement-background': currentTheme?.backgroundColor,
        '--announcement-text': currentTheme?.inactiveColor,
        '--announcement-accent': currentTheme?.activeColor,
        '--announcement-border': currentTheme?.inactiveBorderColor || currentTheme?.borderColor,
        '--announcement-radius': currentTheme?.borderRadius || 'var(--radius-lg)',
      }}
    >
      <div className={styles.header}>
        <h2 id="main-announcement-title" className={styles.title}>
          {t('announcement.title')}
        </h2>

        {canManage && !isEditing && (
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={beginEditing}
              disabled={isMutating}
              aria-label={announcementActionLabel}
              title={announcementActionLabel}
            >
              {announcement ? <EditIcon /> : <WriteIcon />}
            </button>
            {announcement && (
              <button
                type="button"
                className={styles.deleteIconButton}
                onClick={handleClear}
                disabled={isMutating}
                aria-label={clearActionLabel}
                title={clearActionLabel}
              >
                <DeleteIcon />
              </button>
            )}
          </div>
        )}
      </div>

      {isError && !announcement && (
        <p className={styles.error} role="alert">
          {t('announcement.loadError')}
        </p>
      )}

      {isEditing ? (
        <form onSubmit={handleSave}>
          <label className={styles.label} htmlFor="main-announcement-content">
            {t('announcement.contentLabel')}
          </label>
          <textarea
            id="main-announcement-content"
            className={styles.textarea}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setFormError('');
            }}
            maxLength={maxContentLength}
            rows={5}
            autoFocus
          />
          <div className={styles.formMeta}>
            <span className={styles.counter}>
              {maxContentLength ? `${draft.length}/${maxContentLength}` : draft.length}
            </span>
            {formError && <span className={styles.error} role="alert">{formError}</span>}
          </div>
          <div className={styles.formActions}>
            <button type="submit" className={styles.primaryButton} disabled={isMutating}>
              {saveMutation.isPending ? t('announcement.saving') : t('announcement.save')}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={cancelEditing}
              disabled={isMutating}
            >
              {t('announcement.cancel')}
            </button>
          </div>
        </form>
      ) : announcement ? (
        <div>
          <p className={styles.content}>{announcement.content}</p>
          <ContentSafetyButton kind="announcement" contentID={announcement.id} authorID={announcement.updated_by} revision={String(announcement.revision)} />
          {updatedAt && (
            <p className={styles.updatedAt}>{updatedAt}</p>
          )}
          {formError && <p className={styles.error} role="alert">{formError}</p>}
        </div>
      ) : !isError ? (
        <p className={styles.empty}>{t('announcement.empty')}</p>
      ) : null}
    </section>
  );
}
